export interface Change {
  kind: 'same' | 'removed' | 'added';
  text: string;
}

/** Bound work even for a legacy long post; common prefix/suffix still gives an
 * exact reconstruction of both versions when a word matrix would be too large. */
export function revisionDiff(before: string, after: string): Change[] {
  const a = before.match(/\s+|\S+/g) ?? [],
    b = after.match(/\s+|\S+/g) ?? [];
  if (a.length * b.length > 1_000_000) {
    let start = 0,
      end = 0;
    while (start < before.length && start < after.length && before[start] === after[start]) start++;
    while (
      end < before.length - start &&
      end < after.length - start &&
      before[before.length - 1 - end] === after[after.length - 1 - end]
    )
      end++;
    return [
      { kind: 'same', text: before.slice(0, start) },
      { kind: 'removed', text: before.slice(start, before.length - end) },
      { kind: 'added', text: after.slice(start, after.length - end) },
      { kind: 'same', text: end ? before.slice(-end) : '' },
    ].filter((x) => x.text) as Change[];
  }
  const rows = Array.from({ length: a.length + 1 }, () => new Uint16Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i--)
    for (let j = b.length - 1; j >= 0; j--)
      rows[i]![j] =
        a[i] === b[j] ? rows[i + 1]![j + 1]! + 1 : Math.max(rows[i + 1]![j]!, rows[i]![j + 1]!);
  const result: Change[] = [];
  const push = (kind: Change['kind'], text: string) => {
    const last = result[result.length - 1];
    if (last?.kind === kind) last.text += text;
    else result.push({ kind, text });
  };
  let i = 0,
    j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      push('same', a[i++]!);
      j++;
    } else if (j < b.length && (i === a.length || rows[i]![j + 1]! >= rows[i + 1]![j]!))
      push('added', b[j++]!);
    else push('removed', a[i++]!);
  }
  return result;
}
